import { Immutable } from "immer";
import React from "react";
import { WistiaBackgroundVideo } from "tsi-common-react/src/components/WistiaBackgroundVideo/index";

import type {
    TSIAlignedRichTextBlock,
    AlignedHomepageHeroPromoBox as Value,
} from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Sealy's aligned-rich-text section, as emitted by the sealy
 * AlignedHomepageHeroPromoBox override. Unlike the PromotionBlock variant it
 * carries no background-image data attributes, and the hero block has no
 * `spacing` field (the Django `spacing=value.spacing` include was a no-op).
 * Renders nothing when there is no content (mirrors `{% if .content %}`).
 */
const AlignedRichTextSection = (
    props: Immutable<{ value: TSIAlignedRichTextBlock }>,
): React.JSX.Element | null => {
    const v = props.value;
    if (!v.content) {
        return null;
    }
    return (
        <section
            className={concatClassNames([
                "aligned-rich-text-block",
                v.alignment ?? "",
                v.full_width ? "aligned-rich-text-block--full-width" : "",
                v.background_style?.style ?? "",
            ])}
        >
            <RichTextBlock value={v.content} />
        </section>
    );
};

export const AlignedHomepageHeroPromoBox = (
    props: IProps,
): React.JSX.Element | null => {
    const v = props.value;
    const bgUrl = v.background_image?.url;

    return (
        <div
            className={concatClassNames([
                "hero",
                "homepage-hero",
                v.responsive_visibility?.visibility ?? "",
            ])}
            style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        >
            {v.video && <WistiaBackgroundVideo videoId={v.video} />}
            <div className="l-capped-width u-flex-container homepage-hero__content-wrapper">
                <div className="homepage-hero__content">
                    <div className="desktop u-flex-container">
                        <div className="homepage-hero__main-text">
                            {v.main_text && (
                                <AlignedRichTextSection value={v.main_text} />
                            )}
                        </div>
                        <div
                            className={concatClassNames([
                                "homepage-hero__promo-container",
                                v.theme ?? "",
                            ])}
                        >
                            <div className="homepage-hero__promo-box">
                                {v.promo_box && (
                                    <StreamField
                                        tagName={React.Fragment}
                                        value={v.promo_box}
                                        components={{
                                            text: (block) => (
                                                <AlignedRichTextSection
                                                    value={block.value}
                                                />
                                            ),
                                            text_divider: (block) => (
                                                <div className="homepage-hero__promo-box-divider">
                                                    {block.value}
                                                </div>
                                            ),
                                        }}
                                    />
                                )}
                                {v.promo_box_footer && (
                                    <RichTextBlock value={v.promo_box_footer} />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mobile u-flex-container">
                        <div className="homepage-hero__main-text">
                            <RichTextBlock value={v.main_text_mobile ?? ""} />
                        </div>
                        {v.offers && v.offers.length > 0 && (
                            <div className="homepage-hero__offers">
                                {v.offers
                                    .filter(
                                        (offer) =>
                                            !offer.promo_period ||
                                            offer.promo_period.is_promo_active,
                                    )
                                    .map((offer, i) => (
                                        <a
                                            key={i}
                                            href={
                                                offer.relative_link ||
                                                offer.offer?.url ||
                                                ""
                                            }
                                            className="homepage-hero__offer"
                                        >
                                            <div className="homepage-hero__offer--highlight">
                                                <RichTextBlock
                                                    value={
                                                        offer.link_copy ?? ""
                                                    }
                                                />
                                            </div>
                                            <RichTextBlock
                                                value={offer.description ?? ""}
                                            />
                                        </a>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
